import { useState, useEffect, useMemo } from 'react';
import { Customer, CustomerFormData, CustomerStats, CustomerFilters } from '../types';
import {
  getCustomers,
  getCustomerStats,
  getCustomerById,
  createCustomer as createCustomerService,
  updateCustomer as updateCustomerService,
  deleteCustomer as deleteCustomerService,
  reactivateCustomer as reactivateCustomerService,
} from '../services/customers.service';

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CustomerFilters>({ search: '', tag: '', includeInactive: false });

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use real API calls
        const [customersData, statsData] = await Promise.all([
          getCustomers(filters.includeInactive),
          getCustomerStats()
        ]);

        setCustomers(customersData);
        setStats(statsData);
      } catch (err) {
        console.error('Error loading customers:', err);
        setError('Error al cargar los clientes');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters.includeInactive]);

  // Filter customers based on search and tag filters
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesSearch = !filters.search || 
        customer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        customer.document.includes(filters.search) ||
        customer.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        customer.phone.includes(filters.search);

      const matchesTag = !filters.tag || customer.tags.includes(filters.tag);

      return matchesSearch && matchesTag;
    });
  }, [customers, filters]);

  // Create customer
  const createCustomer = async (data: CustomerFormData): Promise<void> => {
    try {
      setError(null);
      const newCustomer = await createCustomerService(data);
      setCustomers(prev => [...prev, newCustomer]);
      
      // Update stats
      const updatedStats = await getCustomerStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Error al crear el cliente');
      throw err;
    }
  };

  // Update customer
  const updateCustomer = async (id: string, data: CustomerFormData): Promise<void> => {
    try {
      setError(null);
      const updatedCustomer = await updateCustomerService(id, data);
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === id ? updatedCustomer : customer
        )
      );
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Error al actualizar el cliente');
      throw err;
    }
  };

  // Delete customer
  const deleteCustomer = async (id: string): Promise<void> => {
    try {
      setError(null);
      await deleteCustomerService(id);
      setCustomers(prev => prev.filter(customer => customer.id !== id));
      
      // Update stats
      const updatedStats = await getCustomerStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Error al eliminar el cliente');
      throw err;
    }
  };

  // Refresh data
  const refreshData = async (): Promise<void> => {
    try {
      setError(null);
      const [customersData, statsData] = await Promise.all([
        getCustomers(),
        getCustomerStats()
      ]);
      
      setCustomers(customersData);
      setStats(statsData);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Error al actualizar los datos');
    }
  };

  // Reactivar cliente eliminado
  const reactivateCustomer = async (id: string): Promise<void> => {
    try {
      setError(null);
      const reactivatedCustomer = await reactivateCustomerService(id);

      // Actualizar el cliente en el estado local
      setCustomers(prev =>
        prev.map(customer =>
          customer.id === id ? reactivatedCustomer : customer
        )
      );

      // Actualizar stats
      const updatedStats = await getCustomerStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error reactivating customer:', err);
      setError('Error al reactivar el cliente');
      throw err;
    }
  };

  // Obtener detalle completo de un cliente (incluye stats calculados)
  const getCustomerDetail = async (id: string): Promise<Customer | null> => {
    try {
      setError(null);
      const customerDetail = await getCustomerById(id);
      return customerDetail;
    } catch (err) {
      console.error('Error getting customer detail:', err);
      setError('Error al obtener el detalle del cliente');
      return null;
    }
  };

  return {
    customers,
    filteredCustomers,
    stats,
    loading,
    error,
    filters,
    setFilters,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    reactivateCustomer,
    getCustomerDetail,
    refreshData,
  };
};